import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Box, Flex, HStack, Text } from '@chakra-ui/react';
import { ArrowUpIcon } from '@chakra-ui/icons';
import { Line, LineChart, ResponsiveContainer, XAxis } from 'recharts';
import { CollabDataContext } from '../../../contexts/collabDataContext';
import { InfluencerCampaignDataContext } from '../../../contexts/InfluencerCampaignDataContext';
import {
  getAllTimeEarnings,
  getFilteredChartData,
  getTotalEarningsForTimeRange,
  TimeRange,
} from '../../../utils/earningsUtils';
import { walletApi } from '../../../services/api/wallet';
import {
  filterLcOrdersByTimeRange,
  mergeLcOrdersIntoChartData,
  sumLcOrderCash,
} from '../utils/lcEarningsUtils';
import { ff } from '../../../theme';
import { MoneyFigure } from '../../../components/ui';
import { walletColors } from '../walletTheme';

const PERIODS: { key: TimeRange; label: string }[] = [
  { key: '1M', label: '1M' },
  { key: '3M', label: '3M' },
  { key: '1Y', label: '1Y' },
  { key: 'ALL', label: 'All' },
];

const cardSx = {
  bg: 'white',
  border: '1px solid',
  borderColor: walletColors.cardBorder,
  borderRadius: '20px',
  p: 4,
  fontFamily: 'display',
} as const;

const WalletEarningsCard: React.FC = () => {
  const collabData = useContext(CollabDataContext);
  const campaignData = useContext(InfluencerCampaignDataContext);
  const [period, setPeriod] = useState<TimeRange>('ALL');
  const [lcOrders, setLcOrders] = useState<Awaited<ReturnType<typeof walletApi.getLcOrders>>['orders']>([]);

  useEffect(() => {
    walletApi.getLcOrders().then((res) => setLcOrders(res.orders)).catch(() => setLcOrders([]));
  }, []);

  const chartData = useMemo(() => {
    const collabChart = getFilteredChartData(collabData, campaignData, period, 'both');
    return mergeLcOrdersIntoChartData(collabChart, lcOrders, period);
  }, [collabData, campaignData, lcOrders, period]);

  const periodTotal = useMemo(() => {
    const lcInRange = filterLcOrdersByTimeRange(lcOrders, period);
    const lcTotal = sumLcOrderCash(lcInRange);
    if (period === 'ALL') {
      return getAllTimeEarnings(collabData, campaignData, 'both') + lcTotal;
    }
    return getTotalEarningsForTimeRange(collabData, campaignData, period, 'both') + lcTotal;
  }, [collabData, campaignData, lcOrders, period]);

  const growthPercent = useMemo(() => {
    if (chartData.length < 2) return null;
    const first = chartData[0].value;
    const last = chartData[chartData.length - 1].value;
    if (first <= 0) return last > 0 ? 100 : null;
    return Math.round(((last - first) / first) * 100);
  }, [chartData]);

  return (
    <Box {...cardSx}>
      <Flex justify="space-between" align="center" mb={3}>
        <Text textStyle="display" fontSize="18px" color={walletColors.textPrimary}>
          Earnings over time
        </Text>
        <HStack spacing={1}>
          {PERIODS.map(({ key, label }) => {
            const active = period === key;
            return (
              <Box
                key={key}
                as="button"
                type="button"
                px="12px"
                h="32px"
                display="flex"
                alignItems="center"
                borderRadius="full"
                bg={active ? ff.ink : ff.grey}
                color={active ? ff.lime : walletColors.textSecondary}
                fontSize="12px"
                fontWeight="600"
                onClick={() => setPeriod(key)}
              >
                {label}
              </Box>
            );
          })}
        </HStack>
      </Flex>

      <Flex align="baseline" gap={2} mb={1} flexWrap="wrap">
        <MoneyFigure
          value={`$${periodTotal.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
          label="Total earned"
          size="30px"
        />
        {growthPercent != null && growthPercent !== 0 && (
          <Flex
            align="center"
            gap={1}
            ml="auto"
            px={2}
            py={0.5}
            borderRadius="full"
            bg={ff.lime}
          >
            <ArrowUpIcon boxSize={2.5} color={ff.ink} />
            <Text fontSize="12px" fontWeight="600" color={ff.ink}>
              {Math.abs(growthPercent)}%
            </Text>
          </Flex>
        )}
      </Flex>

      <Box h="96px" mt={2}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 8, right: 4, left: 0, bottom: 0 }}>
            <XAxis
              dataKey="month"
              axisLine={false}
              tickLine={false}
              tick={{ fill: walletColors.textMuted, fontSize: 11 }}
              interval="preserveStartEnd"
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke={ff.ink}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 5, fill: ff.lime, stroke: ff.ink, strokeWidth: 2 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default WalletEarningsCard;
